import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { SendReminderResponse } from '@academy/types'
import { api } from '../lib/api.js'
import { friendlyError, friendlyErrorName } from '../lib/errors.js'
import { Dialog } from './Dialog.js'
import { Button } from './Button.js'
import { Checkbox } from './Field.js'
import { Icon } from './Icon.js'

interface ReminderInvoice {
  id: string
  studentName: string
  parentName: string | null
  parentHasEmail: boolean
  parentHasPhone: boolean
  academyWantsEmail: boolean
  academyWantsWhatsapp: boolean
}

interface ReminderDialogProps {
  /** The invoice to send a reminder for, or null when closed. */
  invoice: ReminderInvoice | null
  onClose: () => void
  onSent: () => void
}

/**
 * Channel-picker dialog for the "Payment request" action. Lets the admin choose
 * Email / WhatsApp (each enabled only when the academy allows it AND the parent
 * has that contact), sends, then shows the per-channel outcome inline using the
 * shared error catalog's friendly names.
 */
export function ReminderDialog({ invoice, onClose, onSent }: ReminderDialogProps) {
  const emailAvailable = !!invoice && invoice.academyWantsEmail && invoice.parentHasEmail
  const whatsappAvailable = !!invoice && invoice.academyWantsWhatsapp && invoice.parentHasPhone

  const [email, setEmail] = useState(true)
  const [whatsapp, setWhatsapp] = useState(true)
  const [results, setResults] = useState<SendReminderResponse['results'] | null>(null)

  // Reset selections to availability each time a new invoice opens.
  useEffect(() => {
    if (invoice) {
      setEmail(emailAvailable)
      setWhatsapp(whatsappAvailable)
      setResults(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id])

  const send = useMutation({
    mutationFn: async () => {
      const channels: ('email' | 'whatsapp')[] = []
      if (email && emailAvailable) channels.push('email')
      if (whatsapp && whatsappAvailable) channels.push('whatsapp')
      const res = await api<SendReminderResponse>(`/api/v1/fee-invoices/${invoice!.id}/send-reminder`, {
        method: 'POST',
        body: JSON.stringify({ channels }),
      })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: (data) => {
      setResults(data.results)
      onSent()
    },
  })

  const nothingChosen = !(email && emailAvailable) && !(whatsapp && whatsappAvailable)

  return (
    <Dialog
      open={!!invoice}
      onClose={onClose}
      title="Send payment request"
      description={
        invoice
          ? `${invoice.studentName} · ${invoice.parentName ?? 'Parent'}`
          : undefined
      }
      footer={
        results ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => send.mutate()}
              disabled={nothingChosen || send.isPending}
            >
              {send.isPending ? 'Sending…' : 'Send'}
            </Button>
          </>
        )
      }
    >
      {results ? (
        <div className="space-y-2">
          {results.map((r) => (
            <div key={r.channel} className="flex items-start gap-2 text-sm">
              <Icon
                name={r.success ? 'check' : 'alert'}
                size={16}
                className={r.success ? 'text-success mt-0.5' : 'text-danger mt-0.5'}
              />
              <span className="text-fg">
                <span className="capitalize font-medium">{r.channel}</span>{' '}
                {r.success ? (
                  'sent'
                ) : (
                  <span className="text-danger">
                    failed{r.errorCode ? ` — ${friendlyErrorName(r.errorCode)}` : ''}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-fg-muted">Choose how to send this reminder.</p>
          <ChannelOption
            label="Email"
            checked={email && emailAvailable}
            disabled={!emailAvailable}
            onChange={setEmail}
            unavailableReason={
              !invoice?.academyWantsEmail
                ? 'Email is off in payment settings'
                : !invoice?.parentHasEmail
                  ? 'No email on file for this parent'
                  : undefined
            }
          />
          <ChannelOption
            label="WhatsApp"
            checked={whatsapp && whatsappAvailable}
            disabled={!whatsappAvailable}
            onChange={setWhatsapp}
            unavailableReason={
              !invoice?.academyWantsWhatsapp
                ? 'WhatsApp is off in payment settings'
                : !invoice?.parentHasPhone
                  ? 'No phone on file for this parent'
                  : undefined
            }
          />
          {send.isError ? (
            <p className="text-sm text-danger">{(send.error as Error).message}</p>
          ) : null}
        </div>
      )}
    </Dialog>
  )
}

function ChannelOption({
  label,
  checked,
  disabled,
  onChange,
  unavailableReason,
}: {
  label: string
  checked: boolean
  disabled: boolean
  onChange: (v: boolean) => void
  unavailableReason?: string
}) {
  return (
    <div className={disabled ? 'opacity-60' : ''}>
      <Checkbox label={label} checked={checked} disabled={disabled} onChange={onChange} />
      {disabled && unavailableReason ? (
        <p className="ml-6 text-xs text-fg-faint">{unavailableReason}</p>
      ) : null}
    </div>
  )
}
